import { Injectable, inject } from '@angular/core';

import { DeviceService } from '@frontend/vanilla/core';

import { GEOCOMPLY_INIT_SOURCE, MOBILE_APP_CHANNELS } from '../config/appConstants';
import { QuickDepositMessageService } from './quick-deposit-message.service';

export interface IInitializationInput {
    readonly session?: string;
    readonly channelId?: string;
    readonly plcIdentifier?: PLCIdentifier;
    readonly showPLCPopup?: boolean;
    readonly productId?: string;
    readonly geolocationReason?: string;
}
export type PLCIdentifier = 'geocomply' | 'oobeeAndroid' | 'oobeeiOS';
export type EnvironmentName = 'qa2' | 'qa6' | 'qa7' | 'fvt' | 'qa81' | 'qa82' | 'prod';
export enum TriggerGeoLocationOption {
    EventName = 'TRIGGER_GEO_LOCATION',
    Action = 'fetchLocation',
}
@Injectable({
    providedIn: 'root',
})
export class GeocomplyService {
    private deviceService = inject(DeviceService);
    private quickDepositMessageService = inject(QuickDepositMessageService);

    loadGeoComplyScript(userInfo: any) {
        if (!document.getElementById('geo_comply_module')) {
            const { GEOCOMPLY_SCRIPT_URL, GEOCOMPLY_SCRIPT_URL_2, GEOCOMPLY_SCRIPT_URL_MW, GEOCOMPLY_LABEL_NAME: label } = userInfo.configParams;
            if (!GEOCOMPLY_SCRIPT_URL_MW || !GEOCOMPLY_SCRIPT_URL || !GEOCOMPLY_SCRIPT_URL_2) {
                console.info('[GEOCOMPLY]: Something went wrong');
                return;
            }
            this.loadScriptModule(GEOCOMPLY_SCRIPT_URL);

            const { hostname, protocol } = new URL(userInfo?.configParams?.merchant_hostname);
            const parentUrl = `${protocol}//${hostname}`;
            const channel = userInfo?.userInfo?.labelInfo?.channelId;

            if (['MW'].includes(channel)) {
                this.loadScript(GEOCOMPLY_SCRIPT_URL_MW, 'gcoobee');
            }
            const plcIdentifier = this.getPLCIdentifier(channel) ?? 'geocomply';
            const env = this.getEnvironment(hostname);
            const scriptURL = `${parentUrl}${GEOCOMPLY_SCRIPT_URL_2}&geoguard.channelId=${channel}&geoguard.label=${label?.toLowerCase()}&geoguard.environment=${env}&geoguard.plcIdentifier=${plcIdentifier}`;
            this.loadScript(scriptURL, 'appIntegration');
        }
    }

    loadScriptModule(url: string) {
        const scriptContent: string = `import GeoComply from "${url}";
    window.GeoComply = GeoComply;console.info("GeoComply 4.0.0.4 loader is configured...")`;
        const script = document.createElement('script');
        script.type = 'module';
        script.text = scriptContent;
        script.id = 'geo_comply_module';
        document.head.appendChild(script);
    }

    loadScript(url: any, id: any) {
        const myEle = document.getElementById(id);
        if (!myEle) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.setAttribute('id', id);
            script.src = url;
            document.body.appendChild(script);
        }
    }
    getPLCIdentifier(channelId: string): PLCIdentifier {
        if (['MW'].includes(channelId)) {
            if (this.deviceService.isiOS) {
                return 'oobeeiOS';
            }
            return 'oobeeAndroid';
        }
        return 'geocomply';
    }
    getEnvironment(hostName: any): EnvironmentName {
        if (hostName?.startsWith('qa')) {
            const envInfo = hostName.split('.')[0];
            const subDomain: EnvironmentName = envInfo?.toLocaleLowerCase() ?? 'qa2';
            return subDomain;
        } else if (hostName.startsWith('test')) {
            return 'fvt';
        }
        return 'prod';
    }
    getGeoComplyInitializeInput(userData: any): void {
        const channelId = userData?.userInfo?.labelInfo?.channelId || '';
        if (MOBILE_APP_CHANNELS.includes(channelId)) {
            // for native channels
            const geoEvent = {
                eventName: TriggerGeoLocationOption.EventName,
                parameters: {
                    action: TriggerGeoLocationOption.Action,
                    geolocationReason: GEOCOMPLY_INIT_SOURCE,
                },
            };
            if ((window as any)?.JsNativeBridge?.sendMessageToNative) {
                (window as any).JsNativeBridge.sendMessageToNative(JSON.stringify(geoEvent));
            } else if ((window as any)?.messageToNative) {
                (window as any).messageToNative(JSON.stringify(geoEvent));
            } else {
                const message = `action=${TriggerGeoLocationOption.Action}&geolocationReason=${GEOCOMPLY_INIT_SOURCE}`;
                this.quickDepositMessageService.sendOutput(message);
            }
        } else if (this.checkGeoComplyScriptsLoaded()) {
            // For browser
            const input: IInitializationInput = {
                session: userData?.clientInfo?.sessionKey,
                channelId: userData?.userInfo?.labelInfo?.channelId || '',
                plcIdentifier: this.getPLCIdentifier(userData?.userInfo?.labelInfo?.channelId),
                showPLCPopup: false,
                productId: userData?.userInfo?.labelInfo?.productId || 'CASINO',
                geolocationReason: GEOCOMPLY_INIT_SOURCE,
            };
            const geoComplyService = (window as any).geolocationCoordinator;
            geoComplyService?.initialize(input);
        }
    }
    checkGeoComplyScriptsLoaded(): boolean {
        return !!document.getElementById('geo_comply_module');
    }
}
